import { identify as amplitudeIdentify, init as amplitudeInit, setOptOut as amplitudeSetOptOut, setSessionId as amplitudeSetSessionId, track as amplitudeTrack } from '@amplitude/analytics-browser';
import { ConsentService } from './consent';
export const firstTouchStorageKey = 'gv_first_touch';
/** Events tracked while the user has not made a choice yet are kept in memory, then sent if they
 * accept and dropped if they refuse. */
const maxPendingEvents = 50;
export class AnalyticsService {
  static apiKey = null;
  static requireConsent = false;
  static pendingEvents = [];
  static started = false;
  static enabled = false;
  static pendingIdentify = null;
  static pendingSessionId = null;
  static get isEnabled() {
    return AnalyticsService.enabled;
  }
  static init(apiKey, {
    requireConsent = false
  } = {}) {
    if (AnalyticsService.apiKey) return;
    AnalyticsService.apiKey = apiKey;
    AnalyticsService.requireConsent = requireConsent;
    if (!requireConsent) {
      AnalyticsService.start();
      return;
    }
    ConsentService.declareCookies('amplitude', getAmplitudeCookieNames(apiKey));
    AnalyticsService.handleConsent(ConsentService.getStatus('amplitude'));
    ConsentService.subscribe((service, status) => {
      if (service === 'amplitude') AnalyticsService.handleConsent(status);
    });
  }
  static track(name, properties) {
    if (AnalyticsService.enabled) {
      amplitudeTrack(name, typeof properties === 'function' ? properties() : properties);
    } else if (AnalyticsService.requireConsent && ConsentService.getStatus('amplitude') === 'pending' && AnalyticsService.pendingEvents.length < maxPendingEvents) {
      AnalyticsService.pendingEvents.push({
        name,
        properties
      });
    }
  }

  /** Before consent the event is kept in memory only, and sent once the user accepts. */
  static identify(event) {
    if (!AnalyticsService.enabled) {
      AnalyticsService.pendingIdentify = event;
      return;
    }
    amplitudeIdentify(event);
  }
  static setSessionId(sessionId) {
    if (!AnalyticsService.enabled) {
      AnalyticsService.pendingSessionId = sessionId;
      return;
    }
    amplitudeSetSessionId(sessionId);
  }
  static handleConsent(status) {
    if (status === 'granted') AnalyticsService.start();else if (status === 'denied') AnalyticsService.stop();
  }
  static start() {
    if (!AnalyticsService.apiKey || AnalyticsService.enabled) return;
    if (AnalyticsService.started) {
      amplitudeSetOptOut(false);
    } else {
      amplitudeInit(AnalyticsService.apiKey, undefined, {
        serverZone: 'EU',
        defaultTracking: false
      });
      AnalyticsService.started = true;
    }
    AnalyticsService.enabled = true;
    if (AnalyticsService.pendingSessionId !== null) {
      amplitudeSetSessionId(AnalyticsService.pendingSessionId);
      AnalyticsService.pendingSessionId = null;
    }
    if (AnalyticsService.pendingIdentify) {
      amplitudeIdentify(AnalyticsService.pendingIdentify);
      AnalyticsService.pendingIdentify = null;
    }
    const pendingEvents = AnalyticsService.pendingEvents;
    AnalyticsService.pendingEvents = [];
    pendingEvents.forEach(({
      name,
      properties
    }) => AnalyticsService.track(name, properties));
  }
  static stop() {
    AnalyticsService.enabled = false;
    AnalyticsService.pendingIdentify = null;
    AnalyticsService.pendingSessionId = null;
    AnalyticsService.pendingEvents = [];
    if (AnalyticsService.started) amplitudeSetOptOut(true);
    if (AnalyticsService.apiKey) {
      removeCookies(getAmplitudeCookieNames(AnalyticsService.apiKey));
    }
    try {
      localStorage.removeItem(firstTouchStorageKey);
      Object.keys(localStorage).filter(key => key.startsWith('AMP_')).forEach(key => localStorage.removeItem(key));
    } catch {
      console.error('localStorage access is denied');
    }
  }
}
function getAmplitudeCookieNames(apiKey) {
  const suffix = apiKey.substring(0, 10);
  return [`AMP_${suffix}`, `AMP_MKTG_${suffix}`];
}

/** Amplitude writes its cookies on the top-level domain it can reach, so they are removed from the
 * current host and from each of its parent domains. */
function removeCookies(names) {
  if (typeof document === 'undefined') return;
  const hostParts = window.location.hostname.split('.');
  const domains = [''].concat(hostParts.slice(0, -1).map((_, index) => `; domain=.${hostParts.slice(index).join('.')}`));
  names.forEach(name => {
    domains.forEach(domain => {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    });
  });
}